import { useParams, useNavigate } from "react-router-dom";
import { MainLayout } from "@/components/layout/MainLayout";
import { CURATED_CONTENT, SHOW_INFO, CATEGORY_PILL_COLORS } from "@/data/curatedContent";
import { ArticleRightRail } from "@/components/ideas/ArticleRightRail";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowRight, ExternalLink, Lightbulb, ThumbsUp, ThumbsDown } from "lucide-react";
import { useState, useCallback } from "react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { getCuratedContentGrouped } from "@/data/curatedContent";
import { useIsMobile } from "@/hooks/use-mobile";

const EFFORT_STYLES: Record<string, { label: string; className: string }> = {
  Low: { label: "Easy Win", className: "bg-green-500/15 text-green-700 dark:text-green-400 border-green-500/30" },
  Medium: { label: "Medium Effort", className: "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30" },
  High: { label: "Deep Dive", className: "bg-red-500/15 text-red-700 dark:text-red-400 border-red-500/30" },
};

export default function ContentArticlePage() {
  const { contentId } = useParams<{ contentId: string }>();
  const navigate = useNavigate();
  const { user, membership } = useAuth();
  const isMobile = useIsMobile();
  const [feedback, setFeedback] = useState<"up" | "down" | undefined>();

  const item = CURATED_CONTENT.find((c) => c.id === contentId);

  const curatedGroups = getCuratedContentGrouped();
  const itemDate = curatedGroups.find((g) => g.items.some((i) => i.id === contentId))?.date || "";

  const handleFeedback = useCallback(async (fb: "up" | "down") => {
    if (!user || !membership?.org_id || !item) return;
    setFeedback(fb);
    try {
      await supabase.from("idea_feedback").upsert(
        { user_id: user.id, org_id: membership.org_id, idea_title: item.title, idea_category: item.category, feedback: fb, generation_date: itemDate },
        { onConflict: "user_id,idea_title,generation_date" }
      );
    } catch (err) { console.error("Feedback error:", err); }
  }, [user, membership?.org_id, item, itemDate]);

  const handleBuild = useCallback(() => {
    if (!item) return;
    const parts = [item.title, item.description];
    if (item.theStory) parts.push(item.theStory);
    if (item.howTheyDidIt) parts.push(item.howTheyDidIt);
    if (item.theOutput) parts.push(item.theOutput);
    parts.push(`Try This: ${item.tryThis}`);
    navigate("/prompt", { state: { remixContext: { originalPrompt: parts.join("\n\n") } } });
  }, [item, navigate]);

  if (!item) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground mb-2">Article not found</h1>
            <p className="text-muted-foreground mb-4">This content doesn't exist.</p>
            <Button onClick={() => navigate("/ai-ideas")}>Back to Ideas</Button>
          </div>
        </div>
      </MainLayout>
    );
  }

  const show = SHOW_INFO[item.show];
  const effort = EFFORT_STYLES[item.effort];
  const pill = CATEGORY_PILL_COLORS[item.category];

  return (
    <MainLayout>
      <div
        className="mx-auto flex"
        style={{ maxWidth: 1100, padding: isMobile ? "24px 16px" : "40px 24px", gap: 64 }}
      >
        {/* Article column */}
        <article className="flex-1 min-w-0" style={{ maxWidth: 680 }}>
          {/* Show info */}
          <div className="flex items-center gap-3" style={{ marginBottom: 16 }}>
            {show && (
              <img src={show.logo} alt={item.show} className="h-10 w-10 rounded-full object-cover" />
            )}
            <div>
              <p className="text-sm font-semibold text-foreground">{item.show}</p>
              <p className="text-xs text-muted-foreground">{show?.host}</p>
            </div>
          </div>

          {/* Headline */}
          <h1
            className="text-foreground font-bold"
            style={{ fontSize: isMobile ? 28 : 36, lineHeight: 1.2, marginBottom: 12 }}
          >
            {item.title}
          </h1>

          {/* Subtitle */}
          <p
            className="text-foreground/80"
            style={{ fontSize: 18, lineHeight: "28px", marginBottom: 24 }}
          >
            {item.description}
          </p>

          {/* Episode image */}
          <div
            className="w-full overflow-hidden bg-muted"
            style={{ borderRadius: 8, marginBottom: 24 }}
          >
            <img
              src={item.imageUrl}
              alt={item.title}
              className="w-full h-auto object-contain"
              loading="lazy"
            />
          </div>

          {/* Badges */}
          <div className="flex items-center gap-2 flex-wrap" style={{ marginBottom: 32 }}>
            {pill && (
              <Badge variant="secondary" className={`text-xs ${pill.bg} ${pill.text} ${pill.border}`}>
                {item.category}
              </Badge>
            )}
            {effort && (
              <Badge variant="secondary" className={`text-xs ${effort.className}`}>
                {effort.label}
              </Badge>
            )}
          </div>

          {/* The Story */}
          {item.theStory && (
            <section style={{ marginBottom: 32 }}>
              <h2 className="text-lg font-bold text-foreground" style={{ marginBottom: 12 }}>
                The Story
              </h2>
              <p className="text-foreground/85" style={{ fontSize: 16, lineHeight: "26px" }}>
                {item.theStory}
              </p>
            </section>
          )}

          {/* How They Did It */}
          {item.howTheyDidIt && (
            <section style={{ marginBottom: 32 }}>
              <h2 className="text-lg font-bold text-foreground" style={{ marginBottom: 12 }}>
                How They Did It
              </h2>
              <p className="text-foreground/85" style={{ fontSize: 16, lineHeight: "26px" }}>
                {item.howTheyDidIt}
              </p>
            </section>
          )}

          {/* The Output */}
          {item.theOutput && (
            <section style={{ marginBottom: 32 }}>
              <h2 className="text-lg font-bold text-foreground" style={{ marginBottom: 12 }}>
                The Output
              </h2>
              <p className="text-foreground/85" style={{ fontSize: 16, lineHeight: "26px" }}>
                {item.theOutput}
              </p>
            </section>
          )}

          {/* Try This */}
          <div
            className="rounded-lg bg-primary/10 border border-primary/20"
            style={{ padding: 20, marginBottom: 32 }}
          >
            <div className="flex items-center gap-1.5" style={{ marginBottom: 8 }}>
              <Lightbulb className="h-4 w-4 text-primary" />
              <p className="text-xs font-bold uppercase tracking-wide text-primary">Try This</p>
            </div>
            <p className="text-foreground/90" style={{ fontSize: 15, lineHeight: "24px" }}>
              {item.tryThis}
            </p>
          </div>

          {/* Tools */}
          <div style={{ marginBottom: 16 }}>
            <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground" style={{ marginBottom: 4 }}>
              Tools Mentioned
            </p>
            <p className="text-foreground/80" style={{ fontSize: 15 }}>{item.toolsMentioned}</p>
          </div>

          {/* Listen link */}
          <a
            href={item.listenUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
            style={{ marginBottom: 32, display: "inline-flex" }}
          >
            Listen to Episode <ExternalLink className="h-3.5 w-3.5" />
          </a>

          {/* Footer actions */}
          <div
            className="flex items-center justify-between border-t border-border"
            style={{ paddingTop: 24, marginTop: 16, marginBottom: 40 }}
          >
            <div className="flex items-center gap-1">
              <Button
                size="sm"
                variant="ghost"
                className={`h-8 w-8 p-0 ${feedback === "down" ? "text-red-600 dark:text-red-400" : "text-muted-foreground/40 hover:text-red-600 dark:hover:text-red-400"}`}
                onClick={() => handleFeedback("down")}
              >
                <ThumbsDown className={`h-4 w-4 ${feedback === "down" ? "fill-current" : ""}`} />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className={`h-8 w-8 p-0 ${feedback === "up" ? "text-green-600 dark:text-green-400" : "text-muted-foreground/40 hover:text-green-600 dark:hover:text-green-400"}`}
                onClick={() => handleFeedback("up")}
              >
                <ThumbsUp className={`h-4 w-4 ${feedback === "up" ? "fill-current" : ""}`} />
              </Button>
            </div>
            <Button className="gap-1.5" onClick={handleBuild}>
              Build This Prompt <ArrowRight className="h-3.5 w-3.5" />
            </Button>
          </div>

          {/* Mobile: show right rail below article */}
          {isMobile && (
            <div style={{ marginBottom: 40 }}>
              <h3
                className="text-xs font-bold uppercase tracking-widest text-muted-foreground"
                style={{ marginBottom: 20 }}
              >
                More to Read
              </h3>
              <ArticleRightRail currentId={item.id} allItems={CURATED_CONTENT} />
            </div>
          )}
        </article>

        {/* Right rail (desktop only, handled by ArticleRightRail's hidden lg:block) */}
        {!isMobile && (
          <ArticleRightRail currentId={item.id} allItems={CURATED_CONTENT} />
        )}
      </div>
    </MainLayout>
  );
}
